/**
 * SupportResolveModal — the RESOLUTION action: a focus-trapped dialog that closes the loop on a ticket by
 * moving it to `resolu`, stamping resolu_at, and appending the operator's resolution NOTE in-thread (the lab
 * sees the outcome in the chat). The note is required (the server 422s an empty note); the write is
 * version-guarded (a stale version → 409 → "reload then retry"), and resolving a `ferme` ticket is a 409
 * the modal surfaces as "reopen first".
 *
 * On success the parent's live ticket read refetches (the mutation invalidated the management root), so the
 * new statut + the resolution message land in the thread without a manual reload.
 *
 * Accessibility: the textarea carries a real <label> via FormField; the required-note validation renders as
 * an inline message; the confirm button is disabled until the note is non-empty. Token-driven + AA contrast.
 */
import { useEffect, useState } from "react";
import { Button, FormField, Modal, Textarea, useToast } from "@/ui";
import { ApiError } from "@/api";
import { useT } from "@/app";
import { useResolveTicket } from "../supportWorkflow.api";
import type { SupportTicketDetail } from "../support.types";

export interface SupportResolveModalProps {
  open: boolean;
  ticket: SupportTicketDetail;
  onClose: () => void;
}

export function SupportResolveModal({ open, ticket, onClose }: SupportResolveModalProps) {
  const { t } = useT();
  const toast = useToast();
  const resolve = useResolveTicket();
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);

  // Reset the draft whenever the modal (re)opens or the ticket changes, so a prior draft never leaks across.
  useEffect(() => {
    if (open) {
      setNote("");
      setTouched(false);
    }
  }, [open, ticket.id]);

  const trimmed = note.trim();
  const noteMissing = trimmed.length === 0;

  function onConfirm(): void {
    setTouched(true);
    if (noteMissing || resolve.isPending) return;
    resolve.mutate(
      { ticketId: ticket.id, note: trimmed, expected_version: ticket.version },
      {
        onSuccess: () => {
          toast.success(t("mgmt.support.wf.resolve.done"));
          onClose();
        },
        onError: (err) => {
          if (err instanceof ApiError && (err.httpStatus === 409 || err.code === "CONFLICT")) {
            // A closed ticket, or a stale version — the server message distinguishes; show the reopen hint.
            toast.error(ticket.statut === "ferme" ? t("mgmt.support.wf.resolve.closed") : t("mgmt.support.wf.triage.conflict"));
          } else {
            toast.error(t("mgmt.support.wf.triage.error"));
          }
        },
      },
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("mgmt.support.wf.resolve.title", { ref: ticket.reference })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={resolve.isPending}>
            {t("mgmt.support.wf.resolve.cancel")}
          </Button>
          <Button variant="primary" onClick={onConfirm} loading={resolve.isPending} disabled={noteMissing}>
            {t("mgmt.support.wf.resolve.confirm")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <FormField
          label={t("mgmt.support.wf.resolve.note.label")}
          {...(touched && noteMissing ? { error: t("mgmt.support.wf.resolve.note.required") } : {})}
          hint={t("mgmt.support.wf.resolve.note.hint")}
        >
          {(controlProps) => (
            <Textarea
              {...controlProps}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder={t("mgmt.support.wf.resolve.note.placeholder")}
              rows={4}
              maxLength={8000}
              disabled={resolve.isPending}
              invalid={touched && noteMissing}
            />
          )}
        </FormField>
      </div>
    </Modal>
  );
}
